import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "../../components/AdminLayout";
import { getStats, getArticles } from "../../lib/api";
import { 
  FileText, 
  Eye, 
  Sparkles, 
  Globe,
  TrendingUp,
  Clock,
  CheckCircle,
  Edit3,
  Plus
} from "lucide-react";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recentArticles, setRecentArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, articlesRes] = await Promise.all([
          getStats(),
          getArticles({ limit: 5 })
        ]);
        setStats(statsRes.data);
        setRecentArticles(articlesRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const statCards = stats ? [
    { label: "Total de Matérias", value: stats.total_articles, icon: FileText, color: "text-[#FF007F]" },
    { label: "Publicadas", value: stats.published, icon: CheckCircle, color: "text-green-500" },
    { label: "Rascunhos", value: stats.drafts, icon: Edit3, color: "text-amber-500" },
    { label: "Agendadas", value: stats.scheduled, icon: Clock, color: "text-blue-500" },
    { label: "Total de Views", value: stats.total_views, icon: Eye, color: "text-purple-500" },
    { label: "Fontes Monitoradas", value: stats.monitored_sites, icon: Globe, color: "text-emerald-500" },
  ] : [];

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "short",
    });
  };

  return (
    <AdminLayout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white" data-testid="dashboard-title">
              Dashboard
            </h1>
            <p className="text-zinc-500 mt-1">
              Visão geral do portal arrochaplay
            </p>
          </div>
          <Link
            to="/admin/articles/create"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#FF007F] hover:bg-[#FF3399] text-white font-bold rounded-xl transition-all"
            data-testid="create-article-btn"
          >
            <Plus size={20} />
            Nova Matéria
          </Link>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-[#FF007F] border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}

        {/* Stats Grid */}
        {!loading && stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {statCards.map((stat, index) => (
              <div
                key={stat.label}
                className="bg-[#121212] rounded-2xl p-5 border border-white/10 hover:border-white/20 transition-all"
                data-testid={`stat-${index}`}
              >
                <div className={`w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center mb-3 ${stat.color}`}>
                  <stat.icon size={20} />
                </div>
                <p className="text-2xl font-bold text-white">{stat.value.toLocaleString()}</p>
                <p className="text-xs text-zinc-500 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Recent Articles */}
        {!loading && recentArticles.length > 0 && (
          <div className="bg-[#121212] rounded-2xl border border-white/10">
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <div className="flex items-center gap-2">
                <TrendingUp className="text-[#FF007F]" size={20} />
                <h2 className="font-heading text-lg font-bold text-white">
                  Matérias Recentes
                </h2>
              </div>
              <Link
                to="/admin/articles"
                className="text-sm text-[#FF007F] hover:underline"
              >
                Ver todas
              </Link>
            </div>
            <div className="divide-y divide-white/5">
              {recentArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/admin/articles/edit/${article.id}`}
                  className="flex items-center gap-4 p-4 hover:bg-white/5 transition-colors"
                  data-testid={`recent-article-${article.id}`}
                >
                  <img
                    src={article.image_url || "https://images.unsplash.com/photo-1635989193974-102e31f3db9b?w=100"}
                    alt=""
                    className="w-16 h-12 object-cover rounded-lg"
                  />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-medium truncate">
                      {article.title}
                    </h3>
                    <div className="flex items-center gap-3 text-xs text-zinc-500 mt-1">
                      <span>{article.category}</span>
                      <span>{formatDate(article.created_at)}</span>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    article.status === "published" 
                      ? "bg-green-500/20 text-green-500" 
                      : article.status === "scheduled"
                      ? "bg-blue-500/20 text-blue-500"
                      : "bg-amber-500/20 text-amber-500"
                  }`}>
                    {article.status === "published" ? "Publicado" : article.status === "scheduled" ? "Agendado" : "Rascunho"}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
